import { useCallback, useState } from "react";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../../contexts-hooks/hooks/useToster";
import {
  useAllUserList,
  useDeleteRecruiterById,
} from "../../../../store/query/userQuery";

const useRecruiterList = () => {
  const [filterName, setFilterName] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [selectedRecruiterName, setSelectedRecruiterName] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [filterOptions, setFilterOptions] = useState({
    page: 1,
    limit: 5,
    name: "",
  });

  const users = useAllUserList({
    page: filterOptions.page,
    limit: filterOptions.limit,
    search: filterOptions.name,
  });

  const { mutate: deleteRecruiter } = useDeleteRecruiterById();

  const loading = users.isLoading || users.isFetching;

  const headCells = [
    {
      numeric: false,
      disablePadding: true,
      label: "Recruiter Name",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Recruiter Email",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Phone Number",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Role",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Title",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Action",
    },
  ];

  const debouncedFilterChange = useCallback(
    debounce((value) => {
      console.log("Filtering with name:", value);
      setFilterOptions((prev) => ({
        ...prev,
        page: 1,
        name: value,
      }));
    }, 350),
    []
  );

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setFilterName(value);
    debouncedFilterChange(value);
  };

  const handleRowsPerPageChange = (event) =>
    setFilterOptions((prev) => ({
      ...prev,
      limit: event.target.value,
    }));

  const handlePrevPage = () => {
    if (filterOptions.page > 1) {
      setFilterOptions((prev) => ({
        ...prev,
        page: prev.page - 1,
      }));
    }
  };

  const handleNextPage = () => {
    if (users.data?.data?.length >= filterOptions.limit) {
      setFilterOptions((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  const handleClickOpen = (recruiterId, recruiterName) => {
    setSelectedRecruiter(recruiterId);
    setSelectedRecruiterName(recruiterName);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRecruiter(null);
    setSelectedRecruiterName(null);
  };

  const handleDelete = (id) => {
    const recruiterIdToDelete = id || selectedRecruiter;

    if (!recruiterIdToDelete) {
      showToast("No recruiter selected for deletion!", "error");
      return;
    }
    setDeleteLoading(true);

    deleteRecruiter(recruiterIdToDelete, {
      onSuccess: (data) => {
        setDeleteLoading(false);
        setOpen(false);
        setSelectedRecruiter(null);
        setSelectedRecruiterName(null);
        showToast("Recruiter deleted successfully!");
      },
      onError: (error) => {
        console.error("Delete error:", error);
        setDeleteLoading(false);
        const errorMessage =
          error?.response?.data?.message || "Failed to delete recruiter!";
        showToast(errorMessage, "error");
      },
    });
  };

  const handleEditRecruiter = (recruiterId) => {
    navigate(`/app/settings/edit-recruiter/${recruiterId}`);
  };

  const handleAddRecruiter = () => {
    navigate("/app/settings/create-recruiter");
  };

  const handleResetFilters = () => {
    setFilterName("");
    setFilterOptions((prev) => ({
      ...prev,
      name: "",
      page: 1,
    }));
  };

  return {
    filterName,
    filterOptions,
    open,
    selectedRecruiter,
    selectedRecruiterName,
    users,
    headCells,
    handleFilterChange,
    handleRowsPerPageChange,
    handlePrevPage,
    handleNextPage,
    handleClickOpen,
    handleClose,
    handleDelete,
    handleEditRecruiter,
    handleAddRecruiter,
    handleResetFilters,
    loading,
    deleteLoading,
  };
};

export default useRecruiterList;
